/**
 * Upgrade Popup - Redesigned Modern UI with Urgency Countdown
 * 
 * Opens with a reason when a user hits a Pro gate (tools, agents, MCP, sync,
 * favorites, chat limit), and otherwise auto-opens only after real usage.
 * Shows an honest Free vs Pro comparison mirroring useUserSubscription.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    BsCheck2,
    BsLock,
    Button,
    ButtonClose,
} from "@acme/ui";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { teamActiveAtom, upgradeModalAtom, signupDiscountAtom, SIGNUP_DISCOUNT_HOURS, type UpgradeReason } from "../store/user";
import { chatSessionsAtom } from "../store/chat";
import { selectAtom } from "jotai/utils";
import { chatSessionListDefault } from "@acme/util";
import { type SubscriptionPlan, defaultPlans, dayjs } from "@acme/util";
import { api } from "@acme/client";
import { subscribe } from "../setting/SettingsSection/Plans";
import { useUserSubscription } from "../hooks";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";

// =============================================================================
// COUNTDOWN HOOK
// =============================================================================

interface CountdownState {
    /** Whether the discount window is still active */
    isActive: boolean;
    /** Whether the discount has fully expired */
    isExpired: boolean;
    /** Hours remaining (0-48) */
    hours: number;
    /** Minutes remaining (0-59) */
    minutes: number;
    /** Seconds remaining (0-59) */
    seconds: number;
    /** Total ms remaining */
    totalMs: number;
    /** Urgency level for styling: 'low' (>12h), 'medium' (2-12h), 'high' (<2h) */
    urgency: 'low' | 'medium' | 'high';
    /** Formatted string like "47:23:59" */
    formatted: string;
}

/**
 * Hook to compute a countdown from signupDiscountAtom.
 * On first render (if no `firstSeenAt`), it stamps the current time.
 * Returns live countdown state that updates every second.
 */
const useDiscountCountdown = (): CountdownState => {
    const [discountState, setDiscountState] = useAtom(signupDiscountAtom);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Stamp `firstSeenAt` on first ever render
    useEffect(() => {
        if (!discountState.firstSeenAt) {
            setDiscountState({ firstSeenAt: new Date().toISOString() });
        }
    }, [discountState.firstSeenAt, setDiscountState]);

    const deadlineMs = useMemo(() => {
        if (!discountState.firstSeenAt) return 0;
        return new Date(discountState.firstSeenAt).getTime() + SIGNUP_DISCOUNT_HOURS * 60 * 60 * 1000;
    }, [discountState.firstSeenAt]);

    const computeState = useCallback((): CountdownState => {
        if (!deadlineMs) {
            return { isActive: true, isExpired: false, hours: SIGNUP_DISCOUNT_HOURS, minutes: 0, seconds: 0, totalMs: SIGNUP_DISCOUNT_HOURS * 3600000, urgency: 'low', formatted: `${SIGNUP_DISCOUNT_HOURS}:00:00` };
        }
        const remaining = Math.max(0, deadlineMs - Date.now());
        const isExpired = remaining <= 0;
        const hours = Math.floor(remaining / 3600000);
        const minutes = Math.floor((remaining % 3600000) / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);
        const urgency: 'low' | 'medium' | 'high' = hours >= 12 ? 'low' : hours >= 2 ? 'medium' : 'high';
        const formatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        return { isActive: !isExpired, isExpired, hours, minutes, seconds, totalMs: remaining, urgency, formatted };
    }, [deadlineMs]);

    const [state, setState] = useState<CountdownState>(computeState);

    useEffect(() => {
        setState(computeState());
        intervalRef.current = setInterval(() => {
            setState(computeState());
        }, 1000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [computeState]);

    return state;
};

// =============================================================================
// FEATURE HIGHLIGHTS
// =============================================================================

/** Free vs Pro, mirroring the gates in useUserSubscription. */
const comparisonRows: { label: string; free: string | boolean; pro: string | boolean; reason?: UpgradeReason }[] = [
    { label: 'Every model from 8 providers', free: true, pro: true },
    { label: 'Web search & AI tools', free: false, pro: true, reason: 'tools' },
    { label: 'AI agents', free: false, pro: true, reason: 'agents' },
    { label: 'MCP server integrations', free: false, pro: true, reason: 'mcp' },
    { label: 'Cloud sync across devices', free: 'Limited', pro: true, reason: 'sync' },
    { label: 'Saved chats', free: 'Limited', pro: 'More', reason: 'chat-limit' },
    { label: 'Favorites', free: false, pro: true, reason: 'favorites' },
];

/** Headline per trigger, so the popup answers what the user just tried to do. */
const reasonCopy: Record<UpgradeReason, { title: string; subtitle: string }> = {
    tools: { title: 'Web search & tools are Pro', subtitle: 'Let the model search the web, read pages and run tools in your chats.' },
    agents: { title: 'AI agents are Pro', subtitle: 'Hand off multi-step tasks to an agent that plans, uses tools and reports back.' },
    mcp: { title: 'MCP servers are Pro', subtitle: 'Connect your own MCP servers and give every model access to your tools and data.' },
    favorites: { title: 'Favorites are Pro', subtitle: 'Pin the chats you come back to and find them instantly.' },
    sync: { title: 'Sync your chats with Pro', subtitle: 'Your chats live in this browser today. Pro keeps them on every device.' },
    'chat-limit': { title: "You've hit the free chat limit", subtitle: 'Upgrade to keep every conversation instead of deleting old ones.' },
    'note-limit': { title: "You've hit the free note limit", subtitle: 'Upgrade to keep writing with AI-powered notes.' },
};

const includedItems = [
    "Web search, tools & agents",
    "MCP server integrations",
    "Cloud sync across devices",
    "Chrome extension",
    "AI-powered notes",
    "All future updates",
];

/** Auto-open only for people who are actually using the app, and rarely. */
const AUTO_OPEN_MIN_MESSAGES = 10;
const AUTO_OPEN_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000;
const SAMPLE_IDS = new Set(chatSessionListDefault.map(s => s.id));
const engagementAtom = selectAtom(chatSessionsAtom, sessions => {
    let sent = 0, chats = 0;
    for (const session of sessions) {
        if (SAMPLE_IDS.has(session.id)) continue;
        const n = (session.messages || []).filter(m => m.senderRole === 'user').length;
        sent += n;
        if (n > 0) chats++;
    }
    return { sent, chats };
}, (a, b) => a.sent === b.sent && a.chats === b.chats);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const UpgradeModalNew: React.FC = () => {
    const router = useRouter();
    const [{ isOpen, lastOpen, reason }, setUpgradeModal] = useAtom(upgradeModalAtom);
    const { isSubscribe, isLoading } = useUserSubscription();

    const { data: plansData } = api.plan.getAll.useQuery(undefined, {
        staleTime: 1000 * 60 * 10, // 10 minutes
        refetchOnWindowFocus: false,
        keepPreviousData: true, // Prevent flashing to defaultPlans on refetch
    });

    // Only use defaultPlans if we're still loading AND have no cached data
    const plans = plansData ?? defaultPlans;

    const onClose = () => {
        setUpgradeModal({ isOpen: false, lastOpen: new Date(), reason: undefined });
    };

    // Auto-open once a user has real usage (not on first visit, when they have
    // seen no value yet), then at most every few days. Feature gates and the
    // chat limit open it directly with a reason, which is where intent is.
    const engagement = useAtomValue(engagementAtom);
    useEffect(() => {
        if (!router?.isReady || isOpen || isSubscribe === true) return;
        if (engagement.sent < AUTO_OPEN_MIN_MESSAGES) return;
        const last = lastOpen ? dayjs(lastOpen).toDate().getTime() : 0;
        if (Date.now() - last < AUTO_OPEN_COOLDOWN_MS) return;
        setUpgradeModal({ isOpen: true, lastOpen: new Date() });
    }, [engagement.sent, isOpen, isSubscribe, lastOpen, router?.isReady, setUpgradeModal]);

    useEffect(() => {
        if (router?.isReady && !isSubscribe && !isOpen && router?.query?.upgrade) {
            setUpgradeModal({ isOpen: true, lastOpen: new Date() });
            const { upgrade: _upgrade, ...query } = router.query;
            void router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
        }
    }, [router?.isReady, isSubscribe, isLoading, router?.query?.upgrade, router, isOpen, setUpgradeModal]);

    if (!isOpen) return null;

    return (
        <UpgradeModalComponent
            isOpen={isOpen}
            onClose={onClose}
            plans={plans}
            reason={reason}
            engagement={engagement}
        />
    );
};

// =============================================================================
// MODAL COMPONENT
// =============================================================================

type UpgradeModalProps = {
    isOpen: boolean;
    onClose: () => void;
    plans: SubscriptionPlan[];
    reason?: UpgradeReason;
    engagement?: { sent: number; chats: number };
};

export const UpgradeModalComponent: React.FC<UpgradeModalProps> = ({
    isOpen,
    onClose,
    plans,
    reason,
    engagement,
}) => {
    const teamActive = useAtomValue(teamActiveAtom);
    const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
    const [isSubLoading, setIsSubLoading] = useState(false);
    const setUpgradeModal = useSetAtom(upgradeModalAtom);
    const { data: session } = api.auth.getSession.useQuery();
    const countdown = useDiscountCountdown();

    const selectedPlan = plans[selectedPlanIndex];
    const defaultPlan = plans[0] as SubscriptionPlan;
    const { price, originalPrice, discount, interval } = selectedPlan || defaultPlan;
    const discountedPrice = price.toFixed(0);
    const displayOriginalPrice = originalPrice && (originalPrice > price)
        ? originalPrice.toFixed(0)
        : (discount ? ((price / (100 - discount)) * 100).toFixed(0) : '');

    const onUpgrade = async (planId: string) => {
        if (session) {
            try {
                setIsSubLoading(true);
                await subscribe(planId, teamActive?.id || '');
                setIsSubLoading(false);
                setUpgradeModal({ isOpen: false, lastOpen: new Date() });
            } catch (error) {
                console.error("Error during checkout:", error);
                setIsSubLoading(false);
            }
        } else {
            setTimeout(() => {
                void signIn('', { callbackUrl: '/settings?menu=plan' });
            }, 500);
        }
    };

    // Urgency-aware colors
    const urgencyColors = useMemo(() => {
        if (countdown.isExpired) return {
            banner: 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-400',
            headerGradient: 'from-gray-50 to-white dark:from-neutral-800/40 dark:to-neutral-900',
            ctaBg: '!bg-gray-500 hover:!bg-gray-600',
            ctaShadow: 'shadow-gray-500/25',
            pillBg: 'bg-gray-100 dark:bg-neutral-800',
            pillText: 'text-gray-500 dark:text-gray-400',
            dot: 'bg-gray-400',
        };
        if (countdown.urgency === 'high') return {
            banner: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/50',
            headerGradient: 'from-red-50 to-white dark:from-red-900/20 dark:to-neutral-900',
            ctaBg: '!bg-red-600 hover:!bg-red-700',
            ctaShadow: 'shadow-red-500/25',
            pillBg: 'bg-red-100 dark:bg-red-900/40',
            pillText: 'text-red-700 dark:text-red-300',
            dot: 'bg-red-500',
        };
        if (countdown.urgency === 'medium') return {
            banner: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800/50',
            headerGradient: 'from-orange-50 to-white dark:from-orange-900/20 dark:to-neutral-900',
            ctaBg: '!bg-orange-600 hover:!bg-orange-700',
            ctaShadow: 'shadow-orange-500/25',
            pillBg: 'bg-orange-100 dark:bg-orange-900/40',
            pillText: 'text-orange-700 dark:text-orange-300',
            dot: 'bg-orange-500',
        };
        return {
            banner: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
            headerGradient: 'from-blue-50 to-white dark:from-blue-900/20 dark:to-neutral-900',
            ctaBg: '!bg-blue-600 hover:!bg-blue-700',
            ctaShadow: 'shadow-blue-500/25',
            pillBg: 'bg-blue-100 dark:bg-blue-900/40',
            pillText: 'text-blue-700 dark:text-blue-300',
            dot: 'bg-blue-500',
        };
    }, [countdown.isExpired, countdown.urgency]);

    return (
        <AnimatePresence mode="wait">
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-neutral-700 overflow-hidden my-8"
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <ButtonClose onClick={onClose} className="!top-3 !right-3 z-50" />

                        {/* ========== URGENCY COUNTDOWN BANNER ========== */}
                        {countdown.isActive && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                className={`px-4 py-2.5 text-center text-sm font-medium ${urgencyColors.banner}`}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    {countdown.urgency === 'high' && (
                                        <span className="text-base">🔥</span>
                                    )}
                                    <span>
                                        Welcome discount expires in{' '}
                                        <span className="font-mono font-bold tabular-nums tracking-tight">
                                            {countdown.formatted}
                                        </span>
                                    </span>
                                    {countdown.urgency === 'high' && (
                                        <span className="text-base">🔥</span>
                                    )}
                                </div>
                            </motion.div>
                        )}
                        {countdown.isExpired && (
                            <div className={`px-4 py-2.5 text-center text-sm font-medium ${urgencyColors.banner}`}>
                                ⏰ Welcome discount has ended — prices may increase soon
                            </div>
                        )}

                        {/* ========== HEADER ========== */}
                        <div className={`px-6 pt-6 pb-5 text-center bg-gradient-to-b ${urgencyColors.headerGradient}`}>
                            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${urgencyColors.pillBg} ${urgencyColors.pillText} text-xs font-medium mb-3`}>
                                <span className="relative flex h-2 w-2">
                                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${urgencyColors.dot} opacity-75`}></span>
                                    <span className={`relative inline-flex rounded-full h-2 w-2 ${urgencyColors.dot}`}></span>
                                </span>
                                {countdown.isActive
                                    ? (countdown.urgency === 'high' ? 'Ending Soon!' : 'Limited Time Offer')
                                    : 'Special Offer'
                                }
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                {reason ? reasonCopy[reason].title : 'Do more with DapperGPT Pro'}
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                {reason
                                    ? reasonCopy[reason].subtitle
                                    : 'Tools, agents, MCP and sync on top of every model you already use. Keep paying providers directly with your own keys.'}
                            </p>
                            {engagement && engagement.sent > 0 && (
                                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    You&apos;ve sent <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-200">{engagement.sent.toLocaleString()}</span> messages
                                    across <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-200">{engagement.chats}</span> chat{engagement.chats === 1 ? '' : 's'} so far.
                                </p>
                            )}
                        </div>

                        {/* ========== FREE vs PRO ========== */}
                        <div className="px-6 py-4">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-xs text-gray-500 dark:text-gray-400">
                                        <th className="text-left font-medium pb-2"></th>
                                        <th className="font-medium pb-2 w-16">Free</th>
                                        <th className="font-semibold pb-2 w-16 text-blue-600 dark:text-blue-400">Pro</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparisonRows.map(row => {
                                        const highlight = !!reason && row.reason === reason;
                                        const cell = (v: string | boolean) => v === true
                                            ? <BsCheck2 className="mx-auto text-emerald-500 text-base" aria-label="Included" />
                                            : v === false
                                                ? <span className="text-gray-300 dark:text-neutral-600" aria-label="Not included">—</span>
                                                : <span className="text-xs text-gray-600 dark:text-gray-300">{v}</span>;
                                        return (
                                            <tr key={row.label} className={`border-t border-gray-100 dark:border-neutral-800 ${highlight ? 'bg-blue-50 dark:bg-blue-950/40' : ''}`}>
                                                <td className={`py-2 pl-2 ${highlight ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>{row.label}</td>
                                                <td className="py-2 text-center">{cell(row.free)}</td>
                                                <td className="py-2 text-center">{cell(row.pro)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* ========== PRICING ========== */}
                        <div className="px-6 py-4">
                            {/* Plan Tabs */}
                            <div className="flex items-center justify-center gap-2 mb-4">
                                {plans.map((plan, index) => (
                                    <button
                                        key={plan.id}
                                        onClick={() => setSelectedPlanIndex(index)}
                                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${selectedPlanIndex === index
                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                                            : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-neutral-700'
                                            }`}
                                    >
                                        {plan.shortName || plan.interval}
                                    </button>
                                ))}
                            </div>

                            {/* Price Display */}
                            <div className="text-center mb-4">
                                {/* Active discount: show strikethrough original + discounted price */}
                                {countdown.isActive && displayOriginalPrice ? (
                                    <>
                                        <div className="flex items-center justify-center gap-2">
                                            <span className="text-lg text-gray-400 line-through">${displayOriginalPrice}</span>
                                            <span className="text-4xl font-bold text-gray-900 dark:text-white">${discountedPrice}</span>
                                            {interval !== 'lifetime' && (
                                                <span className="text-gray-500 dark:text-gray-400">/{interval === 'halfyearly' ? '6 mo' : 'year'}</span>
                                            )}
                                        </div>
                                        {/* Discount savings badge */}
                                        <motion.div
                                            initial={{ scale: 0.9, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold mt-2 ${countdown.urgency === 'high'
                                                ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                                : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                                                }`}
                                        >
                                            🎉 You save ${(Number(displayOriginalPrice) - Number(discountedPrice)).toFixed(0)}
                                            {countdown.urgency === 'high' && ' — hurry!'}
                                        </motion.div>
                                    </>
                                ) : (
                                    // Expired: just show the price without discount emphasis
                                    <div className="flex items-center justify-center gap-2">
                                        <span className="text-4xl font-bold text-gray-900 dark:text-white">${discountedPrice}</span>
                                        {interval !== 'lifetime' && (
                                            <span className="text-gray-500 dark:text-gray-400">/{interval === 'halfyearly' ? '6 mo' : 'year'}</span>
                                        )}
                                    </div>
                                )}

                                {interval === 'lifetime' && (
                                    <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mt-1">
                                        One-time payment • Forever access
                                    </p>
                                )}
                            </div>

                            {/* CTA Button */}
                            <Button
                                loading={isSubLoading}
                                className={`w-full ${urgencyColors.ctaBg} !text-white !py-3 !text-base !font-semibold !rounded-xl shadow-lg ${urgencyColors.ctaShadow}`}
                                onClick={() => {
                                    if (selectedPlan) void onUpgrade(selectedPlan.id);
                                }}
                            >
                                {countdown.isActive
                                    ? (countdown.urgency === 'high' ? '🔥 Claim Discount Now' : 'Get Started — Save Today')
                                    : 'Get Started Now'
                                }
                            </Button>

                            {/* Urgency sub-text */}
                            {countdown.isActive && (
                                <p className={`text-center text-xs mt-2 ${countdown.urgency === 'high'
                                    ? 'text-red-500 dark:text-red-400 font-medium'
                                    : 'text-gray-500 dark:text-gray-400'
                                    }`}>
                                    {countdown.urgency === 'high'
                                        ? `⚡ Only ${countdown.formatted} left at this price`
                                        : `🔒 Price locks in for all future renewals`
                                    }
                                </p>
                            )}

                            {/* Trust badges */}
                            <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                                <span className="flex items-center gap-1">
                                    <BsLock className="text-sm" />
                                    Secure checkout
                                </span>
                            </div>
                        </div>

                        {/* ========== WHAT'S INCLUDED ========== */}
                        <div className="px-6 py-4 bg-gray-50 dark:bg-neutral-800/50 border-t border-gray-200 dark:border-neutral-700">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                What&apos;s Included
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                {includedItems.map((item, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <BsCheck2 className="text-emerald-500 flex-shrink-0" />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Skip */}
                        <div className="px-6 py-3 text-center border-t border-gray-200 dark:border-neutral-800">
                            <button
                                onClick={onClose}
                                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                            >
                                Maybe later
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default UpgradeModalNew;